// ============================================================================
// OBT F2-A — Tek soru düzenleyici
// ============================================================================
// Yeni bir tasarım sistemi/CSS kurulmaz; mevcut shadcn/ui + Tailwind kullanılır.
// Yeni drag-drop paketi EKLENMEZ; sıralama erişilebilir yukarı/aşağı butonları
// iledir. Koşullu (dallanan) soru backend'de desteklenmediği için YOKTUR.
// ============================================================================

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ChevronUp,
  ChevronDown,
  Copy,
  Trash2,
  Plus,
  X,
} from 'lucide-react';
import {
  OBT_QUESTION_TYPES,
  questionTypeNeedsOptions,
  isDocumentType,
  OBT_DOCUMENT_DISABLED_NOTE,
} from './campaignEnums';

function OptionRow({ option, index, onChange, onRemove, disabled }) {
  return (
    <div className="flex items-center gap-2">
      <Input
        aria-label={`${index + 1}. seçenek metni`}
        value={option.option_label}
        disabled={disabled}
        placeholder={`${index + 1}. seçenek`}
        onChange={(e) => onChange(index, e.target.value)}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        disabled={disabled}
        aria-label={`${index + 1}. seçeneği sil`}
        onClick={() => onRemove(index)}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.question  {_key, question_text, question_type, is_required, photo_required, options[]}
 * @param {number} props.index
 * @param {boolean} props.isFirst
 * @param {boolean} props.isLast
 * @param {boolean} props.disabled  Tasarımcı kilitliyse (yayın sonrası kalıcı sorular)
 */
export default function QuestionEditor({
  question,
  index,
  isFirst,
  isLast,
  disabled = false,
  onChange,
  onRemove,
  onDuplicate,
  onMove,
}) {
  const needsOptions = questionTypeNeedsOptions(question.question_type);

  const patch = (fields) => onChange(question._key, { ...question, ...fields });

  const setOption = (optIndex, text) => {
    const options = [...(question.options || [])];
    options[optIndex] = { ...options[optIndex], option_label: text, option_value: text };
    patch({ options });
  };
  const addOption = () => {
    const options = [...(question.options || []), { option_label: '', option_value: '' }];
    patch({ options });
  };
  const removeOption = (optIndex) => {
    const options = (question.options || []).filter((_, i) => i !== optIndex);
    patch({ options });
  };

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex items-start justify-between gap-2">
          <span className="mt-1 text-sm font-bold text-gray-500">
            Soru {index + 1}
          </span>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={disabled || isFirst}
              aria-label={`${index + 1}. soruyu yukarı taşı`}
              onClick={() => onMove(index, -1)}
            >
              <ChevronUp className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={disabled || isLast}
              aria-label={`${index + 1}. soruyu aşağı taşı`}
              onClick={() => onMove(index, 1)}
            >
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={disabled}
              aria-label={`${index + 1}. soruyu kopyala`}
              onClick={() => onDuplicate(question._key)}
            >
              <Copy className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={disabled}
              aria-label={`${index + 1}. soruyu sil`}
              onClick={() => onRemove(question._key)}
            >
              <Trash2 className="h-4 w-4 text-red-500" aria-hidden="true" />
            </Button>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`q-text-${question._key}`}>Soru metni</Label>
          <Textarea
            id={`q-text-${question._key}`}
            value={question.question_text}
            disabled={disabled}
            placeholder="Örn: Bölgenizdeki havuzlardan çalışmayanlar hangileri?"
            onChange={(e) => patch({ question_text: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`q-type-${question._key}`}>Soru tipi</Label>
            <Select
              value={question.question_type}
              disabled={disabled}
              onValueChange={(value) => {
                const next = { question_type: value };
                // Seçim tipi değilse seçenekler temizlenir.
                if (!questionTypeNeedsOptions(value)) next.options = [];
                else if (!question.options || question.options.length === 0) {
                  next.options = [{ option_label: '', option_value: '' }];
                }
                patch(next);
              }}
            >
              <SelectTrigger id={`q-type-${question._key}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OBT_QUESTION_TYPES.map((t) => (
                  <SelectItem
                    key={t.value}
                    value={t.value}
                    // `document` yalnız YENİ seçim için kapalıdır; mevcut değer
                    // zaten document ise gösterilir (sessiz dönüşüm yok).
                    disabled={t.disabled && question.question_type !== t.value}
                  >
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isDocumentType(question.question_type) && (
              <p
                role="note"
                className="flex items-start gap-1.5 text-xs text-amber-700"
              >
                <span aria-hidden="true">⚠</span>
                <span>{OBT_DOCUMENT_DISABLED_NOTE}</span>
              </p>
            )}
          </div>

          <div className="flex items-end gap-6">
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={question.is_required}
                disabled={disabled}
                onCheckedChange={(v) => patch({ is_required: Boolean(v) })}
                aria-label="Zorunlu soru"
              />
              <span className="text-sm text-gray-700">Zorunlu</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={question.photo_required}
                disabled={disabled}
                onCheckedChange={(v) => patch({ photo_required: Boolean(v) })}
                aria-label="Fotoğraf zorunlu"
              />
              <span className="text-sm text-gray-700">Fotoğraf zorunlu</span>
            </label>
          </div>
        </div>

        {needsOptions && (
          <fieldset className="space-y-2 rounded-lg border border-gray-200 p-3">
            <legend className="px-1 text-xs font-semibold text-gray-500">
              Seçenekler
            </legend>
            {(question.options || []).map((option, optIndex) => (
              <OptionRow
                key={optIndex}
                option={option}
                index={optIndex}
                disabled={disabled}
                onChange={setOption}
                onRemove={removeOption}
              />
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={addOption}
            >
              <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
              Seçenek ekle
            </Button>
          </fieldset>
        )}
      </CardContent>
    </Card>
  );
}
